/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useRef, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// formik components
import { Formik, Form } from "formik";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

// NewUser page components
import Password from "views/users/account/components/ChangePassword/Password";

// NewUser layout schemas for form and form feilds
import validations from "views/users/account/components/ChangePassword/schemas/validations";
import form from "views/users/account/components/ChangePassword/schemas/form";
import initialValues from "views/users/account/components/ChangePassword/schemas/initialValues";

// sweetalert2 components
import Swal from "sweetalert2";

import { putData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

function getSteps() {
  return ["User Info"];
}

function ChangePassword({ signalCloseModal, informParent, checkChanges }) {
  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps();
  const { formId, formField } = form;
  const currentValidation = validations[activeStep];
  const isLastStep = activeStep === steps.length - 1;
  const ref = useRef(null);
  const [formikForm, setFormikForm] = useState({});
  const [formikFormCurrent, setformikFormCurrent] = useState({});
  const [formTouched, setFormTouched] = useState(null);
  const [passwordTouched, setPasswordTouched] = useState(null);

  const href = window.location.href;
  if (!href) return;
  const hrefSplitted = href.split("users/edit/");
  const editUser = hrefSplitted.length > 1 ? parseInt(hrefSplitted[1]) : null;

  const passwordRequirements = [
    "One special characters",
    "Min 6 characters",
    "One number (2 are recommended)",
    "Change it often",
  ];

  const renderPasswordRequirements = passwordRequirements.map((item, key) => {
    const itemKey = `element-${key}`;

    return (
      <SoftBox key={itemKey} component="li" color="text" fontSize="1.25rem" lineHeight={1}>
        <SoftTypography variant="button" color="text" fontWeight="regular" verticalAlign="middle">
          {item}
        </SoftTypography>
      </SoftBox>
    );
  });

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  
  const submitForm = async (values, actions) => {
    await sleep(1000);

    // eslint-disable-next-line no-alert
    //alert(JSON.stringify(values, null, 2));
    let userId = editUser;
    if (!userId) {
      const user = getUserData();
      userId = user.id;
    }

    //console.log("Creation completed, please Update!");
    let setUsersEndpoint = `/api/v1/users/${userId}/password`;

    let objSent = { ...values };
    delete objSent.repeatPassword;
    //delete objSent.roles;

    let response = await putData(setUsersEndpoint, objSent, {
      Authorization: getAuthorizationHeader(),
    });

    if (response) {
      Swal.fire("Request Sent!", `User's Password Updated sucessfully!`, "success");
      if (signalCloseModal) signalCloseModal(false, null);

      actions.setSubmitting(false);
    } else {
      Swal.fire(
        "Error!",
        `Something goes wrong on User's Password Update. Please try again later.`,
        "error"
      );
      if (signalCloseModal) signalCloseModal(false, null);

      actions.setSubmitting(false);
    }

    // if (response) {
    //   signalCloseModal(false, null);

    //   actions.setSubmitting(false);
    // }

    //actions.resetForm();
  };

  const handleSubmit = (values, actions) => {
    submitForm(values, actions);
  };

  
  
  useEffect(() => {
    if (checkChanges && ref && ref.current) {
      if (Object.keys(ref.current.touched).length > 0) informParent(true);
      if (Object.keys(ref.current.touched).length == 0) informParent(false);
    }
  }, [checkChanges]);


  return (
    <Card id="change-password" sx={{ overflow: "visible" }}>
      <SoftBox p={3}>
        <SoftTypography variant="h5">Change Password</SoftTypography>
      </SoftBox>
      <SoftBox pb={3} px={3}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item sx={{ height: "100%", width: "100%" }}>
            <Formik
              innerRef={ref}
              initialValues={initialValues}
              validationSchema={currentValidation}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, isSubmitting }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <Password
                          formData={{
                            values,
                            touched,
                            formField,
                            errors,
                          }}
                          informParent={informParent}
                        />
                      </SoftBox>
                      <SoftBox mt={6} mb={1}>
                        <SoftTypography variant="h5">Password requirements</SoftTypography>
                      </SoftBox>
                      <SoftBox mb={1}>
                        <SoftTypography variant="body2" color="text">
                          Please follow this guide for a strong password
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox component="ul" m={0} pl={3.25} mb={{ xs: 8, sm: 0 }}>
                        {renderPasswordRequirements}
                      </SoftBox>
                      <SoftBox>
                        <Grid container justifyContent="right">
                          <Grid item>
                            <SoftBox ml="auto">
                              <SoftButton
                                type="submit"
                                variant="gradient"
                                color="dark"
                                size="small"
                              >
                                update Password
                              </SoftButton>
                            </SoftBox>
                          </Grid>
                        </Grid>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
}

ChangePassword.propTypes = {
  signalCloseModal: PropTypes.func,
  informParent: PropTypes.func,
  checkChanges: PropTypes.bool,
};
export default ChangePassword;
